import log from 'loglevel';
import { useSnackbar } from 'notistack';
import * as React from 'react'
import { DELEGATE, DELETE, MARK_ALL_INACTIVE, REDO, SET_POSITION, UNDO } from '../fsm/types';
import { MachineContext } from '../fsm/useMachine';
import { determineOffsetAndRatios } from '../utils/getInfo';
import { FlowWindow } from '../utils/globals';
import { useClipboard } from './clipboard';
import _throttle from "lodash.throttle"


declare let window: FlowWindow;

export type StageBoundsType = {
    stage_width: number;
    stage_height: number;
    ratio: number;
}

interface MountListenersOptions {
    audioRef: React.RefObject<null | HTMLAudioElement>
    stageBoundState: [StageBoundsType, React.Dispatch<React.SetStateAction<StageBoundsType>>]
}

export const mountListeners: React.FC<MountListenersOptions> = ({ stageBoundState, audioRef }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [current, send] = React.useContext(MachineContext)
    const [stageBounds, setStageBounds] = stageBoundState
    const clipboardActions = useClipboard();

    const keyListeners = React.useCallback((e: KeyboardEvent) => {
        const active = document.activeElement;
        const isInTextField = active && (
            active.tagName === 'INPUT' ||
            active.tagName === 'TEXTAREA' ||
            (active as HTMLElement).isContentEditable
        );
        if (isInTextField) return;

        if (current.matches({ textboxes: "inactive" })) {
            // Esc?
            if (e.key === "Escape") {
                log.debug("{ textboxes: 'inactive' }, Esc key...sending MARK_ALL_INACTIVE")
                send(MARK_ALL_INACTIVE)
            } else if (
                // Backspace/Delete
                (e.key === "Delete" || e.key === "Backspace")
            ) {
                e.preventDefault()
                if (current.context.currentObject) {
                    e.stopPropagation()
                    log.debug(`Receiving delete shortcut, removing ${current.context.currentObject.id}`)
                    send({ type: DELETE, id: current.context.currentObject.id })
                    enqueueSnackbar(`${current.context.currentObject.type.replace(/^\w/, (c) => c.toUpperCase())
                        } Deleted!`, { autoHideDuration: 2500, persist: false })
                }
                // Command + Shift+ Z
            } else if (e.key === "z" && e.shiftKey && (e.ctrlKey || e.metaKey)) {
                log.debug("Firing redo")
                send({ type: REDO })
                // Command + Z
            } else if (e.key === "z" && (e.ctrlKey || e.metaKey)) {
                log.debug("Firing undo")
                send({ type: UNDO })
            }
            else if ((e.key === "e" || e.key === "u" || e.key === "8")
                && e.shiftKey
                && (e.ctrlKey || e.metaKey)) {
                log.debug("e and shift and command hit")
                try {
                    audioRef.current!.play()
                } catch (error) {
                    log.warn("Skipping Tushar audio :(...No audio permissions")
                }
                // @ts-ignore
                Array.from(document.getElementsByClassName("hidden_button"))[0].click()
                // command + shit + e -- plays sound
            }
            // Command + S
            else if (e.key === "s" && (e.ctrlKey || e.metaKey)) {
                // send(UPLOAD_DRAFTS)
            } else if (e.keyCode === 38 && current.context.currentObject) {
                // up arrow
                let currObject = current.context.currentObject
                const incrementValue = e.shiftKey ? 10 : 1
                log.debug("Received Up Arrow, moving up 1")
                send({ type: DELEGATE, payload: { type: SET_POSITION, x: currObject.frame.x, y: clamp(currObject.frame.y - incrementValue, 0, current.context.flow.viewSize.height) }, id: currObject.id })
            }
            else if (e.keyCode === 40 && current.context.currentObject) {
                // down arrow
                let currObject = current.context.currentObject
                const incrementValue = e.shiftKey ? 10 : 1
                log.debug("Received Down Arrow, moving up 1")
                send({ type: DELEGATE, payload: { type: SET_POSITION, x: currObject.frame.x, y: clamp(currObject.frame.y + incrementValue, 0, current.context.flow.viewSize.height) }, id: currObject.id })

            }
            else if (e.keyCode === 37 && current.context.currentObject) {
                // left arrow
                let currObject = current.context.currentObject
                const incrementValue = e.shiftKey ? 10 : 1
                log.debug("Received Left Arrow, moving up 1")
                send({ type: DELEGATE, payload: { type: SET_POSITION, x: clamp(currObject.frame.x - incrementValue, 0, current.context.flow.viewSize.width), y: currObject.frame.y }, id: currObject.id })
            }
            else if (e.keyCode === 39 && current.context.currentObject) {
                let currObject = current.context.currentObject
                const incrementValue = e.shiftKey ? 10 : 1
                log.debug("Received Right Arrow, moving up 1")
                send({ type: DELEGATE, payload: { type: SET_POSITION, x: clamp(currObject.frame.x + incrementValue, 0, current.context.flow.viewSize.width), y: currObject.frame.y }, id: currObject.id })
            }
            else if (current.context.currentObject && e.key === 'x' && (e.ctrlKey || e.metaKey)) {
                log.debug("Cutting current object")
                clipboardActions.cut()
            }
            else if (current.context.currentObject && e.key === 'c' && (e.ctrlKey || e.metaKey)) {
                log.debug("Copying current object")
                clipboardActions.copy()
            }
            else if (e.key === 'v' && (e.ctrlKey || e.metaKey)) {
                log.debug("Pasting current object")
                clipboardActions.paste()
            }
        }
    }, [current, send, enqueueSnackbar, clipboardActions, audioRef])

    const throttledKeyListeners = React.useMemo(() => _throttle(keyListeners, 300), [keyListeners]);

    useEventListener("keydown", throttledKeyListeners);


    useEventListener("paste", clipboardActions.systemPasteListener)
    useEventListener("beforeunload", (e) => {
        if (window.newFlow) return ''
        // If this is not a new flow, and there was no upload
        if (!current.matches({ logic: "complete" })) {
            e.preventDefault()
            const savemsg =
                "Please save your changes to Presentation Settings before closing FlowVella Editor.";
            return savemsg;
        }
        return ''
    })

    const handleCanvasResize = () => {
        let newVals = determineOffsetAndRatios(current.context.flow.viewSize.width, current.context.flow.viewSize.height)
        setStageBounds(
            newVals.ratio >= 1 ? { ...newVals, ratio: 1 / newVals.ratio } : newVals,
        )
    }

    // Dynamically adjust size and scale of canvas at runtime
    try {
        useEventListener("resize", handleCanvasResize);
    } catch (error) {
        if (error.message == "ResizeObserver loop limit exceeded") {
            console.log("Ignoring ResizeObserver loop limit exceeded...benign error");
        }
    }

    useEventListener("load", handleCanvasResize);

    return null;
}

// Hook
export function useEventListener(eventName: string, handler: (e: KeyboardEvent) => void, element = window) {
    // Create a ref that stores handler
    const savedHandler = React.useRef<(e: Event) => void>(null);
    // Update ref.current value if handler changes.
    // This allows our effect below to always get latest handler ...
    // ... without us needing to pass it in effect deps array ...
    // ... and potentially cause effect to re-run every render.
    React.useEffect(() => {
        //@ts-ignore
        savedHandler.current = handler;
    }, [handler]);

    React.useEffect(
        () => {
            // Make sure element supports addEventListener
            // On
            const isSupported = element && element.addEventListener;
            if (!isSupported) return;
            // @ts-ignore Create event listener that calls handler function stored in ref
            const eventListener = (event: Event) => savedHandler.current(event);
            try {
                // Add event listener
                element.addEventListener(eventName, eventListener);
            } catch (error) {
                if (error.message == "ResizeObserver loop limit exceeded") {
                    console.log("Ignoring ResizeObserver loop limit exceeded...benign error");
                }
            }
            // Remove event listener on cleanup

            return () => {
                try {
                    element.removeEventListener(eventName, eventListener);
                } catch (error) {
                    log.warn(error)
                }
            };
        },
        [eventName, element] // Re-run if eventName or element changes
    );

};


const clamp = (value: number, low: number, high: number) => {
    if (value <= low) {
        return value + 1
    }
    else if (value >= high) {
        return value - 1
    }
    return value
}